import { useState, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { parseEntitlementDeniedFromStreamEvent } from '@/components/gating/parse-entitlement-denied'
import type { EntitlementDeniedPayload } from '@/components/gating/types'
import { useAuth } from './use-auth'
import { SAVED_OPPORTUNITIES_QUERY_KEY } from './use-saved-opportunities'
import { SSE_ENDPOINT } from '../constants'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { SearchCandidatesData, SearchCandidatesResponse, CandidateMatch } from './use-briefs'
import { SearchProgressStage, LiveCandidate } from '../components/recruiter/search-progress'

export type UnifiedCandidate = LiveCandidate & { full?: CandidateMatch }

export interface OpportunityMatch {
  rank: number
  opportunity_id: string
  role_title: string
  company_name: string
  final_score: number
  recommendation?: string
  found_by?: string[]
  was_llm_judged?: boolean
  evaluation_id?: string | null
  analysis_headline?: string | null
}

export interface OpportunitySearchResponse {
  session_id: string
  evaluation_run_id?: string | null
  total_candidates_evaluated: number
  llm_judged_count: number
  top_matches: OpportunityMatch[]
  strategies_used?: string[]
  search_summary?: string
}

function mergeOnlyCandidateToMatch(candidate: UnifiedCandidate, rank: number): CandidateMatch {
  const score = candidate.score ?? 0
  const foundBy =
    candidate.strategies ?? (candidate.strategy ? [candidate.strategy] : undefined)
  return {
    rank,
    profile_id: candidate.profile_id,
    candidate_name: candidate.name,
    final_score: score,
    structured_score: score,
    semantic_score: score * 0.8,
    was_llm_judged: false,
    found_by: foundBy,
    avatar_url: candidate.full?.avatar_url ?? null,
  }
}

function mergeResultsIntoCandidates(
  prev: UnifiedCandidate[],
  topMatches: CandidateMatch[],
): UnifiedCandidate[] {
  const topIds = new Set((topMatches || []).map((m) => m.profile_id))
  const merged: UnifiedCandidate[] = (topMatches || []).map((m) => {
    const live = prev.find((c) => c.profile_id === m.profile_id)
    return {
      ...(live || {
        profile_id: m.profile_id,
        name: m.candidate_name,
        score: m.final_score,
        rank: m.rank,
      }),
      full: m,
      state: 'analyzed' as const,
    }
  })

  const mergeOnly = prev
    .filter((c) => !topIds.has(c.profile_id))
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))

  let nextRank = merged.length
  for (const candidate of mergeOnly) {
    nextRank += 1
    const rank = candidate.rank ?? nextRank
    merged.push({
      ...candidate,
      rank,
      state: 'analyzed' as const,
      full: mergeOnlyCandidateToMatch(candidate, rank),
    })
  }

  return merged.sort(
    (a, b) => (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER),
  )
}

function parseBatchProgress(content: string): { current: number; total: number; batchNumber?: number; totalBatches?: number } | null {
  const m = content.match(/Scoring\s+(\d+)-(\d+)\s+of\s+(\d+)/i) ?? content.match(/(\d+)-(\d+)\s+of\s+(\d+)/)
  if (m) {
    const current = parseInt(m[1], 10)
    const end = parseInt(m[2], 10)
    const total = parseInt(m[3], 10)
    return { current, total: end - current + 1, totalBatches: total > 0 ? Math.ceil(total / (end - current + 1)) : undefined }
  }
  const ext = content.match(/Extracting CV text from (\d+) profiles/i)
  if (ext) return { current: 0, total: parseInt(ext[1], 10) }
  return null
}

export type SmartSearchError = Error & { denial?: EntitlementDeniedPayload }

function errorFromSearchEvent(data: Record<string, unknown>): SmartSearchError {
  const denial = parseEntitlementDeniedFromStreamEvent(data)
  const message =
    (typeof data.content === 'string' && data.content) ||
    (typeof data.text === 'string' && data.text) ||
    'Search error'
  const err = new Error(message) as SmartSearchError
  if (denial) err.denial = denial
  return err
}

export function useSmartSearch() {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const [stages, setStages] = useState<SearchProgressStage[]>([])
  const [candidates, setCandidates] = useState<UnifiedCandidate[]>([])
  const [opportunities, setOpportunities] = useState<OpportunityMatch[]>([])
  const [strategyProgress, setStrategyProgress] = useState<Record<string, { candidatesFound: number; totalProcessed?: number; isComplete?: boolean }>>({})
  const [parallelOperations, setParallelOperations] = useState<number | undefined>(undefined)
  const [processingSpeed, setProcessingSpeed] = useState<number | undefined>(undefined)
  const [isSearching, setIsSearching] = useState(false)
  const [results, setResults] = useState<SearchCandidatesResponse | null>(null)
  const [opportunityResults, setOpportunityResults] = useState<OpportunitySearchResponse | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [shortlistCapApplied, setShortlistCapApplied] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const searchStartRef = useRef<number | null>(null)
  const candidateCountRef = useRef(0)
  const opportunityCountRef = useRef(0)

  const activeOpportunityIdRef = useRef<string | null>(null)

  const invalidateSearchSessionQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['search-sessions'] })
    const opportunityId = activeOpportunityIdRef.current
    if (opportunityId) {
      // Job-scoped runs link evaluations to applications server-side; refresh board surfaces.
      void queryClient.invalidateQueries({ queryKey: ['opportunity-applicants', opportunityId] })
    }
  }, [queryClient])

  const startSmartSearch = useCallback(
    async (briefId: string | null, data: SearchCandidatesData, opportunityId?: string | null) => {
      activeOpportunityIdRef.current = opportunityId ?? null
      if (!token) {
        setError(new Error('Not authenticated'))
        return
      }

      // Reset state (candidate search)
      setStages([])
      setCandidates([])
      setOpportunities([])
      setStrategyProgress({})
      setParallelOperations(undefined)
      setProcessingSpeed(undefined)
      setResults(null)
      setOpportunityResults(null)
      setError(null)
      setIsSearching(true)
      searchStartRef.current = Date.now()
      candidateCountRef.current = 0

      // Abort any existing search
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      const abortController = new AbortController()
      abortControllerRef.current = abortController

      try {
        await fetchEventSource(SSE_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: 'search_candidates',
            ...(briefId && { brief_id: briefId }),
            ...(opportunityId && { opportunity_id: opportunityId }),
            ...data,
          }),
          signal: abortController.signal,

          async onopen(response) {
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`)
            }
          },

          onmessage(event) {
            if (!event.data?.trim()) return

            try {
              const data = JSON.parse(event.data)

              // Handle search events
              if (event.event === 'content_block_delta') {
                const delta = data.delta
                if (delta?.type === 'error') {
                  setError(new Error(typeof delta.text === 'string' ? delta.text : 'Search error'))
                  setIsSearching(false)
                  return
                }
                if (delta?.search_event) {
                  const searchEvent = delta.search_event

                  switch (searchEvent.type) {
                    case 'search_thinking':
                      setStages((prev) => [
                        ...prev,
                        {
                          stage: searchEvent.stage || 'thinking',
                          content: searchEvent.content || '',
                          timestamp: Date.now(),
                          strategy: searchEvent.strategy,
                        },
                      ])
                      break

                    case 'search_progress':
                    case 'search_stage': {
                      const content = searchEvent.content || ''
                      const strategy = searchEvent.strategy
                      setStages((prev) => [
                        ...prev,
                        {
                          stage: searchEvent.stage || 'progress',
                          content,
                          timestamp: Date.now(),
                          strategy,
                        },
                      ])
                      const batch = parseBatchProgress(content)
                      if (strategy && batch) {
                        setStrategyProgress((prev) => ({
                          ...prev,
                          [strategy]: {
                            candidatesFound: prev[strategy]?.candidatesFound ?? 0,
                            totalProcessed: batch.total,
                            isComplete: false,
                          },
                        }))
                      }
                      const foundMatch = content.match(/Found\s+(\d+)\s+candidates?/i)
                      if (strategy && foundMatch) {
                        setStrategyProgress((prev) => ({
                          ...prev,
                          [strategy]: {
                            candidatesFound: parseInt(foundMatch[1], 10),
                            totalProcessed: prev[strategy]?.totalProcessed,
                            isComplete: true,
                          },
                        }))
                      }
                      if (content.toLowerCase().includes('parallel') && content.match(/\d+/)) {
                        const n = content.match(/(\d+)\s+concurrent|concurrent\s+(\d+)|(\d+)\s+profiles/i)
                        if (n) setParallelOperations(parseInt(n[1] ?? n[2] ?? n[3], 10))
                      }
                      break
                    }

                    case 'search_candidate': {
                      const c = searchEvent.candidate
                      if (!c) break
                      const profileId = c.profile_id ?? String(Date.now())
                      const isFullPayload = c.llm_reasoning !== undefined || c.structured_score !== undefined
                      const state = searchEvent.stage === 'analyzing' ? 'analyzed' : searchEvent.stage === 'merged' ? 'found' : 'found'
                      setCandidates((prev) => {
                        const idx = prev.findIndex((x) => x.profile_id === profileId)
                        const partial: LiveCandidate = {
                          profile_id: profileId,
                          name: c.name ?? c.candidate_name ?? 'Unknown',
                          score: c.score ?? c.final_score ?? 0,
                          rank: c.rank,
                          strategy: c.strategy,
                          strategies: c.strategies,
                          state,
                        }
                        let full: CandidateMatch | undefined
                        if (isFullPayload) {
                          full = {
                            rank: c.rank ?? 0,
                            profile_id: profileId,
                            candidate_name: c.candidate_name ?? c.name ?? 'Unknown',
                            final_score: c.final_score ?? c.score ?? 0,
                            structured_score: c.structured_score ?? 0,
                            semantic_score: c.semantic_score ?? 0,
                            llm_score: c.llm_score,
                            llm_reasoning: c.llm_reasoning,
                            was_llm_judged: c.was_llm_judged ?? false,
                            found_by: c.found_by,
                            category: c.category,
                            avatar_url:
                              typeof c.avatar_url === 'string' && c.avatar_url.trim()
                                ? c.avatar_url.trim()
                                : null,
                          }
                        }
                        const next: UnifiedCandidate = { ...partial, ...(full && { full }) }
                        if (idx >= 0) {
                          const out = [...prev]
                          out[idx] = { ...out[idx], ...next, state }
                          return out
                        }
                        const newCount = prev.length + 1
                        candidateCountRef.current = newCount
                        const elapsed = (Date.now() - (searchStartRef.current ?? Date.now())) / 1000
                        if (elapsed > 0) setProcessingSpeed(newCount / elapsed)
                        return [...prev, next]
                      })
                      if (searchEvent.strategy) {
                        setStrategyProgress((prev) => {
                          const s = prev[searchEvent.strategy] ?? { candidatesFound: 0 }
                          return { ...prev, [searchEvent.strategy]: { ...s, candidatesFound: s.candidatesFound + 1 } }
                        })
                      }
                      break
                    }

                    case 'search_candidate_analysis_start': {
                      const c = searchEvent.candidate
                      if (!c?.profile_id) break
                      setCandidates((prev) => {
                        const idx = prev.findIndex((x) => x.profile_id === c.profile_id)
                        if (idx < 0) {
                          return [...prev, { profile_id: c.profile_id, name: c.name || 'Unknown', score: 0, rank: c.rank, state: 'analyzing' as const }]
                        }
                        const out = [...prev]
                        out[idx] = { ...out[idx], state: 'analyzing' }
                        return out
                      })
                      break
                    }

                    case 'search_candidate_cv_extracting': {
                      const c = searchEvent.candidate
                      if (!c?.profile_id) break
                      setCandidates((prev) => {
                        const idx = prev.findIndex((x) => x.profile_id === c.profile_id)
                        if (idx < 0) {
                          return [...prev, { profile_id: c.profile_id, name: c.name || 'Unknown', score: 0, state: 'cvExtracting' as const }]
                        }
                        const out = [...prev]
                        out[idx] = { ...out[idx], state: 'cvExtracting' }
                        return out
                      })
                      break
                    }

                    case 'search_complete': {
                      const topMatches = searchEvent.top_matches || []
                      const isOpportunitySearch = topMatches.length > 0 && topMatches[0]?.opportunity_id != null
                      if (isOpportunitySearch) {
                        const payload: OpportunitySearchResponse = {
                          session_id: searchEvent.session_id,
                          total_candidates_evaluated: searchEvent.total_candidates_evaluated || 0,
                          llm_judged_count: searchEvent.llm_judged_count || 0,
                          top_matches: topMatches as OpportunityMatch[],
                        }
                        setOpportunityResults(payload)
                        setOpportunities(topMatches as OpportunityMatch[])
                      } else {
                        const payload = {
                          session_id: searchEvent.session_id,
                          total_candidates_evaluated: searchEvent.total_candidates_evaluated || 0,
                          llm_judged_count: searchEvent.llm_judged_count || 0,
                          top_matches: topMatches,
                          strategies_used: searchEvent.strategies_used,
                          search_summary: searchEvent.search_summary,
                          previous_run_at: searchEvent.previous_run_at ?? null,
                        }
                        setResults(payload)
                        setCandidates((prev) => mergeResultsIntoCandidates(prev, payload.top_matches))
                      }
                      if (searchEvent.session_id) invalidateSearchSessionQueries()
                      setIsSearching(false)
                      break
                    }

                    case 'search_opportunity': {
                      const o = searchEvent.opportunity
                      if (!o?.opportunity_id) break
                      setOpportunities((prev) => {
                        const idx = prev.findIndex((x) => x.opportunity_id === o.opportunity_id)
                        const match: OpportunityMatch = {
                          rank: o.rank ?? prev.length + 1,
                          opportunity_id: o.opportunity_id,
                          role_title: o.role_title ?? '',
                          company_name: o.company_name ?? '',
                          final_score: o.final_score ?? o.score ?? 0,
                          found_by: o.strategies ?? o.found_by,
                        }
                        if (idx >= 0) {
                          const out = [...prev]
                          out[idx] = { ...out[idx], ...match }
                          return out
                        }
                        return [...prev, match]
                      })
                      break
                    }
                  }
                }
              } else if (data.type === 'search_complete') {
                const topMatches = data.top_matches || []
                const isOpportunitySearch = topMatches.length > 0 && topMatches[0]?.opportunity_id != null
                if (isOpportunitySearch) {
                  const payload: OpportunitySearchResponse = {
                    session_id: data.session_id,
                    total_candidates_evaluated: data.total_candidates_evaluated || 0,
                    llm_judged_count: data.llm_judged_count || 0,
                    top_matches: topMatches as OpportunityMatch[],
                  }
                  setOpportunityResults(payload)
                  setOpportunities(topMatches as OpportunityMatch[])
                } else {
                  const payload = {
                    session_id: data.session_id,
                    total_candidates_evaluated: data.total_candidates_evaluated || 0,
                    llm_judged_count: data.llm_judged_count || 0,
                    top_matches: topMatches,
                    top_candidates: data.top_candidates,
                    compelling_candidates: data.compelling_candidates,
                    precluded_candidates: data.precluded_candidates,
                    strategies_used: data.strategies_used,
                    search_summary: data.search_summary,
                    previous_run_at: data.previous_run_at ?? null,
                  }
                  setResults(payload)
                  setCandidates((prev) => mergeResultsIntoCandidates(prev, payload.top_matches))
                }
                if (data.session_id) invalidateSearchSessionQueries()
                setIsSearching(false)
              } else if (data.type === 'error') {
                setError(errorFromSearchEvent(data as Record<string, unknown>))
                setIsSearching(false)
              }
            } catch (err) {
              console.error('[Analysis] Parse error:', err)
            }
          },

          onclose() {
            setIsSearching(false)
          },

          onerror(error) {
            console.error('[Analysis] Error:', error)
            setError(new Error(error.message || 'Search failed'))
            setIsSearching(false)
          },
        })
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error('[Analysis] Failed:', err)
          setError(new Error(err.message || 'Search failed'))
        }
        setIsSearching(false)
      }
    },
    [token, invalidateSearchSessionQueries]
  )

  const startSearchOpportunities = useCallback(
    async (profileId: string, instructions?: string) => {
      if (!token) {
        setError(new Error('Not authenticated'))
        return
      }

      setStages([])
      setCandidates([])
      setOpportunities([])
      setStrategyProgress({})
      setParallelOperations(undefined)
      setProcessingSpeed(undefined)
      setResults(null)
      setOpportunityResults(null)
      setError(null)
      setIsSearching(true)
      searchStartRef.current = Date.now()
      opportunityCountRef.current = 0

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      const abortController = new AbortController()
      abortControllerRef.current = abortController

      try {
        await fetchEventSource(SSE_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: 'search_opportunities',
            profile_id: profileId,
            instructions: instructions || '',
          }),
          signal: abortController.signal,

          async onopen(response) {
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
          },

          onmessage(event) {
            if (!event.data?.trim()) return
            try {
              const data = JSON.parse(event.data)
              if (event.event === 'content_block_delta') {
                const delta = data.delta
                if (delta?.type === 'error') {
                  setError(new Error(typeof delta.text === 'string' ? delta.text : 'Search error'))
                  setIsSearching(false)
                  return
                }
                if (delta?.search_event) {
                  const searchEvent = delta.search_event
                  switch (searchEvent.type) {
                    case 'search_thinking':
                      setStages((prev) => [...prev, { stage: searchEvent.stage || 'thinking', content: searchEvent.content || '', timestamp: Date.now(), strategy: searchEvent.strategy }])
                      break
                    case 'search_progress':
                    case 'search_stage': {
                      const content = searchEvent.content || ''
                      const strategy = searchEvent.strategy
                      setStages((prev) => [...prev, { stage: searchEvent.stage || 'progress', content, timestamp: Date.now(), strategy }])
                      const foundMatch = content.match(/Found\s+(\d+)\s+opportunities/i)
                      if (strategy && foundMatch) {
                        setStrategyProgress((prev) => ({
                          ...prev,
                          [strategy]: {
                            candidatesFound: parseInt(foundMatch[1], 10),
                            totalProcessed: prev[strategy]?.totalProcessed,
                            isComplete: true,
                          },
                        }))
                      }
                      const mergedMatch = content.match(/Merged to\s+(\d+)\s+unique opportunities/i)
                      if (mergedMatch) {
                        setStrategyProgress((prev) => ({
                          ...prev,
                          merging: {
                            candidatesFound: parseInt(mergedMatch[1], 10),
                            isComplete: true,
                          },
                        }))
                      }
                      break
                    }
                    case 'search_opportunity': {
                      const o = searchEvent.opportunity
                      if (!o?.opportunity_id) break
                      setOpportunities((prev) => {
                        const idx = prev.findIndex((x) => x.opportunity_id === o.opportunity_id)
                        const match: OpportunityMatch = {
                          rank: o.rank ?? prev.length + 1,
                          opportunity_id: o.opportunity_id,
                          role_title: o.role_title ?? '',
                          company_name: o.company_name ?? '',
                          final_score: o.final_score ?? o.score ?? 0,
                          found_by: o.strategies ?? o.found_by,
                        }
                        if (idx >= 0) {
                          const out = [...prev]
                          out[idx] = { ...out[idx], ...match }
                          return out
                        }
                        const newCount = prev.length + 1
                        opportunityCountRef.current = newCount
                        const elapsed = (Date.now() - (searchStartRef.current ?? Date.now())) / 1000
                        if (elapsed > 0) setProcessingSpeed(newCount / elapsed)
                        return [...prev, match]
                      })
                      break
                    }
                    case 'search_complete': {
                      const topMatches = searchEvent.top_matches || []
                      const payload: OpportunitySearchResponse = {
                        session_id: searchEvent.session_id,
                        total_candidates_evaluated: searchEvent.total_candidates_evaluated || 0,
                        llm_judged_count: searchEvent.llm_judged_count || 0,
                        top_matches: topMatches as OpportunityMatch[],
                        strategies_used: searchEvent.strategies_used,
                      }
                      setOpportunityResults(payload)
                      setOpportunities(topMatches as OpportunityMatch[])
                      if (searchEvent.session_id) invalidateSearchSessionQueries()
                      setIsSearching(false)
                      break
                    }
                  }
                }
              } else if (data.type === 'search_complete') {
                const topMatches = data.top_matches || []
                const payload: OpportunitySearchResponse = {
                  session_id: data.session_id,
                  total_candidates_evaluated: data.total_candidates_evaluated || 0,
                  llm_judged_count: data.llm_judged_count || 0,
                  top_matches: topMatches as OpportunityMatch[],
                  strategies_used: data.strategies_used,
                }
                setOpportunityResults(payload)
                setOpportunities(topMatches as OpportunityMatch[])
                if (data.session_id) invalidateSearchSessionQueries()
                setIsSearching(false)
              } else if (data.type === 'error') {
                setError(errorFromSearchEvent(data as Record<string, unknown>))
                setIsSearching(false)
              }
            } catch (err) {
              console.error('[Opportunity Search] Parse error:', err)
            }
          },

          onclose() {
            setIsSearching(false)
          },

          onerror(err) {
            console.error('[Opportunity Search] Error:', err)
            setError(new Error(err.message || 'Search failed'))
            setIsSearching(false)
          },
        })
      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error('[Opportunity Search] Failed:', err)
          setError(new Error(err.message || 'Search failed'))
        }
        setIsSearching(false)
      }
    },
    [token, invalidateSearchSessionQueries]
  )

  const startShortlistAnalysis = useCallback(
    async (profileId: string) => {
      if (!token) {
        setError(new Error('Not authenticated'))
        return
      }

      setError(null)
      setShortlistCapApplied(false)
      setIsSearching(true)

      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }

      const abortController = new AbortController()
      abortControllerRef.current = abortController
      let completed = false

      const finishShortlistAnalysis = (event: Record<string, unknown>) => {
        completed = true
        if (event.shortlist_cap_applied) {
          setShortlistCapApplied(true)
        }
        const judged = Number(event.llm_judged_count ?? 0)
        if (judged === 0) {
          setError(
            (prev) =>
              prev ??
              new Error(
                'No jobs were analysed. If you use OpenAI from an unsupported region, switch network or update your API key.',
              ),
          )
        }
        void queryClient.invalidateQueries({ queryKey: SAVED_OPPORTUNITIES_QUERY_KEY })
        void queryClient.invalidateQueries({ queryKey: ['my-applications'] })
        setIsSearching(false)
      }

      try {
        await fetchEventSource(SSE_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            action: 'analyse_shortlist',
            profile_id: profileId,
          }),
          signal: abortController.signal,

          async onopen(response) {
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
          },

          onmessage(event) {
            if (!event.data?.trim()) return
            try {
              const data = JSON.parse(event.data)
              if (event.event === 'content_block_delta') {
                const delta = data.delta
                if (delta?.type === 'error') {
                  setError(
                    new Error(
                      typeof delta.text === 'string' ? delta.text : 'Saved-job analysis failed',
                    ),
                  )
                  completed = true
                  setIsSearching(false)
                  return
                }
                const searchEvent = delta?.search_event
                if (searchEvent?.type === 'search_complete') {
                  finishShortlistAnalysis(searchEvent as Record<string, unknown>)
                } else if (searchEvent?.type === 'error') {
                  setError(errorFromSearchEvent(searchEvent as Record<string, unknown>))
                  completed = true
                  setIsSearching(false)
                }
              } else if (data.type === 'search_complete') {
                finishShortlistAnalysis(data as Record<string, unknown>)
              } else if (data.type === 'error') {
                setError(errorFromSearchEvent(data as Record<string, unknown>))
                completed = true
                setIsSearching(false)
              }
            } catch (err) {
              console.error('[Saved-job analysis] Parse error:', err)
            }
          },

          onclose() {
            if (!completed) {
              setIsSearching(false)
            }
          },

          onerror(err) {
            console.error('[Saved-job analysis] Error:', err)
            setError(new Error(err.message || 'Saved-job analysis failed'))
            setIsSearching(false)
          },
        })
      } catch (err: unknown) {
        if (err instanceof Error && err.name !== 'AbortError') {
          setError(new Error(err.message || 'Saved-job analysis failed'))
        }
        setIsSearching(false)
      }
    },
    [token, queryClient],
  )

  const cancelSearch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsSearching(false)
  }, [])

  return {
    startSmartSearch,
    startSearchOpportunities,
    startShortlistAnalysis,
    cancelSearch,
    stages,
    candidates,
    opportunities,
    strategyProgress,
    parallelOperations,
    processingSpeed,
    isSearching,
    shortlistCapApplied,
    results,
    opportunityResults,
    error,
  }
}
